/**
 * Date + time picker combo!
 *
 * DateTimeSlotPicker is timezone-aware, and works with 12/24h formats. If the
 * component is passed a different timezone through props after it's been
 * mounted, it'll update the date and time to that new timezone and go on with
 * its business. The component also ensures the selected date and time are in
 * the future, no matter the timezone.
 *
 * This component is uncontrolled (except for the timezone prop), and uses the
 * onSubmit callback in props to let its parent know of any scheduling action.
 */

import React, { useState } from 'react';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezonePlugin from 'dayjs/plugin/timezone';
import styled from 'styled-components';
import { InputDate } from '@bufferapp/components';
import { Button, Notice, Text } from '@bufferapp/ui';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import Select from './shared/Select';

import TimePicker from './TimePicker';
import SlotPicker from './SlotPicker';
import { metaDataPropType } from './ComposerPropTypes';

dayjs.extend(utc);
dayjs.extend(timezonePlugin);

const DateTimePicker = styled.div`
  padding: 10px 16px;
  text-align: center;
`;

const PickerSwitchButton = styled(Button)`
  margin: 8px auto 0;
`;

const SubmitButton = styled(Button)`
  margin: 16px auto 0;
`;
const TimezoneStyled = styled.div`
  margin-top: 8px;
  font-size: 12px;
`;

const SlotPickerSelect = styled(Select)`
  margin-top: 16px;
`;

const NoticeStyled = styled(Notice)`
  margin-top: 10px;
  text-align: left;
  padding: 8px;
  width: calc(100% - 16px);
`;

const TextStyled = styled(Text)`
  margin: 0 0 0 -5px;
`;

const DateTimeSlotPicker = ({
  timezone,
  shouldUse24hTime,
  onSubmit,
  onClick,
  metaData,
  onChange,
  isDateOnly,
  isDropdownItem,
  initialDateTime,
  isSlotPickingAvailable,
  availableSchedulesSlotsForDay,
  isPinnedToSlot,
  submitButtonCopy,
  weekStartsMonday,
  doSelectedProfilesHaveSlots,
  showDraftScheduleNotice,
}) => {
  const isTimezoneSet = !!timezone;
  const todayDate = new Date().setSeconds(0); // Seconds must be 0 for precise scheduling
  // Determine initial date and time for the picker
  const todayDayjs = isTimezoneSet
    ? dayjs.tz(todayDate, timezone)
    : dayjs(todayDate);
  const preselectedDateTime =
    initialDateTime || todayDayjs.clone().add(3, 'hours');
  const draftScheduleNotice =
    "This draft won't be published until you schedule it as a finished post";

  const [selectedDateTime, setSelectedDateTime] = useState(preselectedDateTime);
  const [emptyByDefault, setEmptyByDefault] = useState(false);
  const [displaySlotPicker, setDisplaySlotPicker] = useState(false);

  const shouldDisplaySlotPickerOnInit = () => {
    if (!isSlotPickingAvailable || !availableSchedulesSlotsForDay) return false;

    const selectedTimestamp = selectedDateTime.unix();
    const isAnySlotSelected = availableSchedulesSlotsForDay.some(
      (slot) => slot.timestamp === selectedTimestamp
    );

    return isAnySlotSelected && isPinnedToSlot;
  };

  const updateDate = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const day = date.getDate();
    let newSelectedDateTime;

    if (isDateOnly) {
      newSelectedDateTime = selectedDateTime
        .year(year)
        .month(month)
        .date(day)
        .startOf('day');
    } else {
      newSelectedDateTime = selectedDateTime.year(year).month(month).date(day);
    }

    const pinnedToSlot = shouldDisplaySlotPickerOnInit();
    setSelectedDateTime(newSelectedDateTime);
    onChange(newSelectedDateTime, pinnedToSlot);
  };

  const updateTime = (time) => {
    const hours = time.hour();
    const minutes = time.minute();
    const pinnedToSlot = shouldDisplaySlotPickerOnInit();
    let newSelectedDateTime = selectedDateTime.hour(hours).minute(minutes);
    newSelectedDateTime = timezone
      ? dayjs(newSelectedDateTime).tz(timezone, true)
      : newSelectedDateTime;

    setSelectedDateTime(newSelectedDateTime);

    onChange(newSelectedDateTime, pinnedToSlot);
  };

  const updateTimeFromTimestamp = (timestamp) => {
    const pinnedToSlot = shouldDisplaySlotPickerOnInit();
    let newSelectedDateTime = dayjs.unix(timestamp);
    if (timezone) newSelectedDateTime = newSelectedDateTime.tz(timezone);
    setSelectedDateTime(newSelectedDateTime);

    onChange(newSelectedDateTime, pinnedToSlot);
  };

  // Set to UTC, then negate the original offset
  const stripOffsetFromMoment = (m) => m.clone().utc().add(m.utcOffset(), 'm');

  const onSlotPickerChange = (timestamp) => {
    updateTimeFromTimestamp(timestamp);
    setEmptyByDefault(false);
  };

  const onSwitchToSlotPickerClick = () => {
    // Update UI state
    setDisplaySlotPicker(true);

    // If a slot is available for the current date time, update store to
    // transition from custom time to pinned update mode
    if (!isSlotPickingAvailable || !availableSchedulesSlotsForDay) return;

    const selectedTimestamp = selectedDateTime.unix();
    const pinnedToSlot = availableSchedulesSlotsForDay.some(
      (slot) => slot.timestamp === selectedTimestamp
    );
    onChange(selectedDateTime, pinnedToSlot);

    setEmptyByDefault(true);
  };

  const onSwitchToTimePickerClick = () => {
    setDisplaySlotPicker(false);
    onChange(selectedDateTime, false);
  };

  const onClickSubmit = () => {
    if (!initialDateTime) {
      onChange(selectedDateTime, false);
    }
    onSubmit(selectedDateTime.unix());
  };

  const selectedDays = {
    selected: (day) => {
      const selectedDateTimeNoOffset = stripOffsetFromMoment(selectedDateTime);
      const dayNoOffset = stripOffsetFromMoment(dayjs(day));
      return selectedDateTimeNoOffset.isSame(dayNoOffset, 'day');
    },
  };

  const shouldDisableSetButton =
    (!isDateOnly && !selectedDateTime.isAfter(new Date())) ||
    (isDateOnly && selectedDateTime.unix() < dayjs().startOf('day').unix());
  const formattedTimezone = isTimezoneSet
    ? timezone.replace('/', ': ').replace('_', ' ')
    : null;

  return (
    <DateTimePicker onClick={onClick} data-testid="date-time-picker">
      <InputDate
        initialMonth={new Date()}
        onDayClick={updateDate}
        selectedDays={selectedDays.selected}
        firstDayOfWeek={weekStartsMonday ? 1 : 0}
      />

      {!isDateOnly && !displaySlotPicker && (
        <TimePicker
          shouldUse24hTime={shouldUse24hTime}
          time={selectedDateTime}
          timezone={timezone}
          onChange={updateTime}
        />
      )}

      {!isDateOnly &&
        !displaySlotPicker &&
        isSlotPickingAvailable &&
        doSelectedProfilesHaveSlots && (
          <PickerSwitchButton
            type="secondary"
            size="small"
            label="Switch to schedule slots"
            onClick={onSwitchToSlotPickerClick}
          />
        )}

      {!isDateOnly &&
        displaySlotPicker &&
        isSlotPickingAvailable &&
        (availableSchedulesSlotsForDay ? (
          <SlotPicker
            metaData={metaData}
            shouldUse24hTime={shouldUse24hTime}
            timezone={timezone}
            slots={availableSchedulesSlotsForDay}
            slot={selectedDateTime}
            onChange={onSlotPickerChange}
            emptyByDefault={emptyByDefault}
          />
        ) : (
          <SlotPickerSelect disabled value="">
            <option value="">Loading slots…</option>
          </SlotPickerSelect>
        ))}

      {!isDateOnly && displaySlotPicker && isSlotPickingAvailable && (
        <PickerSwitchButton
          type="secondary"
          size="small"
          label="Switch to custom time"
          onClick={onSwitchToTimePickerClick}
        />
      )}

      {!isDropdownItem && (
        <SubmitButton
          type="primary"
          size="small"
          label={submitButtonCopy}
          onClick={onClickSubmit}
          disabled={shouldDisableSetButton}
        />
      )}
      {/* Below is needed for the dropdown to work after we implemented */}
      {/* scrollbar in the composer. When we decide in the future that */}
      {/* Composer should no longer be in the modal, we can remove */}
      {/* isDropdownItem prop and below conditional */}
      {isDropdownItem && (
        <DropdownMenu.Item>
          <SubmitButton
            type="primary"
            size="small"
            label={submitButtonCopy}
            onClick={onClickSubmit}
            disabled={shouldDisableSetButton}
          />
        </DropdownMenu.Item>
      )}

      {isTimezoneSet && <TimezoneStyled>{formattedTimezone}</TimezoneStyled>}

      {showDraftScheduleNotice && (
        <NoticeStyled type="warning" disableAnimation>
          <TextStyled type="p">{draftScheduleNotice}</TextStyled>
        </NoticeStyled>
      )}
    </DateTimePicker>
  );
};

DateTimeSlotPicker.propTypes = {
  shouldUse24hTime: PropTypes.bool.isRequired,
  onSubmit: PropTypes.func.isRequired,
  isSlotPickingAvailable: PropTypes.bool,
  doSelectedProfilesHaveSlots: PropTypes.bool,
  onClick: PropTypes.func,
  onChange: PropTypes.func,
  timezone: PropTypes.string,
  weekStartsMonday: PropTypes.bool.isRequired,
  availableSchedulesSlotsForDay: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.arrayOf(
      PropTypes.shape({
        isSlotFree: PropTypes.bool.isRequired,
        timestamp: PropTypes.number.isRequired,
      })
    ),
  ]),
  isPinnedToSlot: PropTypes.bool,
  metaData: metaDataPropType,
  initialDateTime: PropTypes.instanceOf(dayjs),
  submitButtonCopy: PropTypes.string,
  showDraftScheduleNotice: PropTypes.bool,
  isDateOnly: PropTypes.bool,
  isDropdownItem: PropTypes.bool,
};

DateTimeSlotPicker.defaultProps = {
  isSlotPickingAvailable: false,
  doSelectedProfilesHaveSlots: false,
  onClick: () => {},
  onChange: () => {},
  submitButtonCopy: 'Schedule',
  availableSchedulesSlotsForDay: undefined,
  isPinnedToSlot: null,
  metaData: undefined,
  showDraftScheduleNotice: false,
  isDateOnly: false,
  isDropdownItem: false,
  timezone: null,
  initialDateTime: null,
};

export default DateTimeSlotPicker;
